// Considered: ComposerIndexRailMolecule — rejected because it is a section navigator (anchors +
// completion marks), not a searchable source list of draggable blocks with a note per block.
// Considered: GalleryCardPreviewMolecule / composer gallery — rejected because that is a card grid
// of templates, not a compact palette meant to be dragged into a grid cell. See spec Reuse Audit
// (arc-editor-canvas-v1.md).
import React, { useRef } from 'react';
import { useTranslation } from 'localization';
import SearchBarAtom from '../../atoms/SearchBar/SearchBarAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import IconOrGlyph from '../../atoms/Glyph/IconOrGlyph';
import MenuAtom, { type MenuHandle } from '../../atoms/menu/menu';
import './BlockPaletteMolecule.scss';

/** The `dataTransfer` type a palette tile writes its id under on dragstart. Firefox refuses to
 * start a drag that carries no data, and a drop target that reads the id (`ArcIntentRowMolecule`)
 * reads this key. */
export const BLOCK_DRAG_MIME = 'text/block';

export interface BlockPaletteItem {
  id: string;
  /** Already-localized block name (`b.name`, `arc-app.jsx:378`). */
  name: string;
  /** A PrimeIcons class or an Iconify name — passed to `IconOrGlyph`. */
  glyph: string;
  /** Already-localized one-line description of what dropping this block adds (`b.note`). */
  note: string;
}

/** One choice in a tile's keyboard "Place in…" menu — a drop drag would have targeted this cell. */
export interface BlockPlacementTarget {
  value: string;
  /** Already-localized (e.g. "Doing × Manager"). */
  label: string;
}

/** One labelled run of blocks (`AC_LIB_GROUPS`, `arc-app.jsx:367-385`). */
export interface BlockPaletteGroup {
  id: string;
  /** Already-localized group label (`{g}`). */
  label: string;
  items: BlockPaletteItem[];
}

export interface BlockPaletteMoleculeProps {
  query: string;
  onQueryChange: (value: string) => void;
  groups: BlockPaletteGroup[];
  onAddStage: () => void;
  /** Disables dragging out and the add-stage affordance (`locked` — a read-only arc has no palette). */
  disabled?: boolean;
  /** Id of the tile mid-drag (`dragging === b.id`, `arc-app.jsx:374`) — carries `data-dragging` for styling. */
  draggingId?: string;
  onTileDragStart?: (id: string) => void;
  onTileDragEnd?: () => void;
  /** Per-tile choices for the keyboard "Place in…" menu (a stage, or a stage × lane). Returning
   * an empty array means the tile needs no placement choice — Enter/Space calls `onTileActivate`
   * directly instead (e.g. a stage tile, which always appends after the last stage). */
  getPlacementTargets?: (item: BlockPaletteItem) => BlockPlacementTarget[];
  /** Fires when a "Place in…" menu choice is picked for a tile. */
  onTilePlace?: (item: BlockPaletteItem, target: BlockPlacementTarget) => void;
  /** Fires on Enter/Space for a tile whose `getPlacementTargets` returns none. */
  onTileActivate?: (item: BlockPaletteItem) => void;
  className?: string;
}

/** The index region's palette: search + a searchable, draggable source list of blocks, closed by
 * "Add a stage" (`arc-app.jsx:360-390`). */
const BlockPaletteMolecule: React.FC<BlockPaletteMoleculeProps> = ({
  query,
  onQueryChange,
  groups,
  onAddStage,
  disabled = false,
  draggingId,
  onTileDragStart,
  onTileDragEnd,
  getPlacementTargets,
  onTilePlace,
  onTileActivate,
  className = '',
}) => {
  const { t } = useTranslation();
  const searchPlaceholder = t('studio.arcEditor.palette.searchPlaceholder');
  const menuRefs = useRef<Record<string, MenuHandle | null>>({});

  /** The pointer and keyboard paths converge here: a tile with placement choices opens its
   * "Place in…" menu, one without (a stage tile) activates directly. */
  const activateTile = (
    item: BlockPaletteItem,
    targets: BlockPlacementTarget[],
    event: React.SyntheticEvent
  ) => {
    if (disabled) return;
    if (targets.length > 0) {
      menuRefs.current[item.id]?.show(event);
    } else {
      onTileActivate?.(item);
    }
  };

  return (
    <aside
      className={`block-palette ${className}`.trim()}
      data-region="index"
      aria-label={t('studio.arcEditor.palette.ariaLabel')}
      data-testid="block-palette"
    >
      <div className="block-palette__head" data-container="index > group:blocks">
        <span className="block-palette__title" data-datum="index.blocks" data-kind="heading">
          {t('studio.arcEditor.palette.heading')}
        </span>
        <span
          className="block-palette__hint"
          data-datum="index.drag-into-a-lane-or-use-the-lane-s-add"
          data-kind="hint"
        >
          {t('studio.arcEditor.palette.hint')}
        </span>
      </div>
      <SearchBarAtom
        value={query}
        onChange={onQueryChange}
        placeholder={searchPlaceholder}
        ariaLabel={searchPlaceholder}
        disabled={disabled}
        inputDatum="index.search-blocks"
      />
      <div className="block-palette__scroll" data-container="index > list">
        {groups.map((group) => (
          <div
            key={group.id}
            className="block-palette__group"
            data-container="index > list > each(AC_LIB_GROUPS) > group"
          >
            <span
              className="block-palette__group-label"
              data-datum="index.g"
              data-kind="label"
            >
              {group.label}
              <span className="block-palette__group-count" aria-hidden>
                {group.items.length}
              </span>
            </span>
            {group.items.map((item) => {
              const targets = disabled ? [] : getPlacementTargets?.(item) ?? [];
              return (
                <div
                  key={item.id}
                  className="block-palette__tile"
                  draggable={!disabled}
                  data-dragging={draggingId === item.id ? 'true' : undefined}
                  onDragStart={(e) => {
                    e.dataTransfer.setData(BLOCK_DRAG_MIME, item.id);
                    e.dataTransfer.effectAllowed = 'copy';
                    onTileDragStart?.(item.id);
                  }}
                  onDragEnd={() => onTileDragEnd?.()}
                  data-datum="index.tile"
                  data-kind="tile"
                  data-container="index > list > each(AC_LIB_GROUPS) > group > each(items)"
                  data-testid={`block-palette-tile-${item.id}`}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-haspopup={targets.length > 0 ? 'menu' : undefined}
                  aria-label={t('studio.arcEditor.palette.placeInAriaLabel', { name: item.name })}
                  onClick={(e) => activateTile(item, targets, e)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    activateTile(item, targets, e);
                  }}
                >
                  <span
                    className="block-palette__tile-top"
                    data-container="index > list > each(AC_LIB_GROUPS) > group > each(items) > group"
                  >
                    <IconOrGlyph icon={item.glyph} className="block-palette__tile-icon" />
                    <strong data-datum="index.b-name" data-kind="strong">
                      {item.name}
                    </strong>
                  </span>
                  <span className="block-palette__tile-note" data-datum="index.b-note" data-kind="hint">
                    {item.note}
                  </span>
                  {targets.length > 0 && (
                    <MenuAtom
                      popup
                      ref={(el) => {
                        menuRefs.current[item.id] = el;
                      }}
                      model={targets.map((target) => ({
                        label: target.label,
                        command: () => onTilePlace?.(item, target),
                      }))}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <ButtonAtom
        variant="secondary"
        size="small"
        icon="pi pi-plus"
        label={t('studio.arcEditor.palette.addStage')}
        onClick={onAddStage}
        disabled={disabled}
        className="block-palette__add-stage"
        data-datum="index.add-a-stage"
        data-kind="button"
        data-testid="block-palette-add-stage"
      />
    </aside>
  );
};

BlockPaletteMolecule.displayName = 'BlockPaletteMolecule';

export default BlockPaletteMolecule;
